import { ProCard } from '@ant-design/pro-components'
import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import { type AdTeam, type AdTeamIdType, AdTeamSchema } from '@app/icare/entities/adTeam/schema/ADTeam'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { EditDrawer } from '@app/icare/form/EditDrawer'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import { type SectionDef, useSectionFlow } from '@pkg/form/useSectionFlow'
import { zodDefaultInstance } from '@pkg/shared/form'
import { createTypedModal, type EditModalProps, type EditModalResult, type EditModalShow } from '@pkg/ui/modal'
import { useStore } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { Flex } from 'antd'

const sections = [{ name: 'general', title: t`Allgemein` }] as const satisfies readonly SectionDef[]

type AdTeamSection = (typeof sections)[number]['name']

export const AdTeamEditDrawer = createTypedModal<
  EditModalProps<AdTeam, AdTeamSection, AdTeamIdType> | undefined,
  EditModalResult<AdTeam>,
  EditModalShow<AdTeam, AdTeamSection, AdTeamIdType>
>(props => {
  const modal = AdTeamEditDrawer.useModal()
  const { data, isLoading } = useQuery(localQueryEntityOptions(props?.id))
  const mode = props?.id ? 'edit' : 'create'
  const defaultValues = data ?? {
    ...zodDefaultInstance(AdTeamSchema),
    ...props?.defaults,
  }

  const form = useAppForm({
    ...appFormOptions(AdTeamSchema, {
      defaultValues,
    }),
    onSubmit: async ({ value }) => {
      await sync.mutate(value)
      modal.resolveRemove({ value, mode })
    },
  })

  const flow = useSectionFlow({ sections, section: props?.section })

  const isDirty = useStore(form.store, s => s.isDirty)
  const isSubmitting = useStore(form.store, s => s.isSubmitting)
  const isValid = useStore(form.store, s => s.isFormValid)

  return (
    <SectionFlow flow={flow} onSubmit={() => void form.handleSubmit()} onCancel={() => modal.rejectRemove()}>
      <EditDrawer
        modal={modal}
        flow={flow}
        loading={isLoading}
        isDirty={isDirty}
        extra={<SectionFlow.Actions isSaving={isSubmitting} canSubmit={isValid} />}
      >
        <form.AppForm>
          <form.ProForm>
            <SectionFlow.Step name="general">
              <ProCard>
                <Flex vertical gap="small">
                  <form.AppField name="name" children={field => <field.Text label={t`Name`} />} />
                </Flex>
              </ProCard>
            </SectionFlow.Step>
          </form.ProForm>
        </form.AppForm>
      </EditDrawer>
    </SectionFlow>
  )
})
